import { useEffect, useState, useMemo } from 'react';
import clsx from 'clsx';
import { useLocation } from 'react-router-dom';
import { Collapse, IconButton, ListItemButton, ListItemText, styled, alpha } from '@mui/material';

import NavLinkAdapter from '@fuse/core/NavLinkAdapter';
import { Icon } from 'app/main/components';
import FuseNavBadge from '../../FuseNavBadge';
import FuseNavItem from '../../FuseNavItem';
import type { FuseNavProps } from '../../types/FuseNavProps';

const Root = styled('ul', { shouldForwardProp: name => name !== 'itemPadding' })<{ itemPadding: number }>(
	({ theme, itemPadding }) => ({
		padding: 0,
		'&.open': {},
		'& > .fuse-list-item': {
			height: 40,
			width: '100%',
			borderRadius: '6px',
			margin: '0 0 4px 0',
			paddingRight: 12,
			paddingLeft: itemPadding > 80 ? 80 : itemPadding,
			color: alpha(theme.palette.text.primary, 0.7),
			'&:hover': {
				color: theme.palette.text.primary
			},
			'& > .fuse-list-item-icon': {
				marginRight: 12,
				color: 'inherit'
			}
		}
	})
);

function needsToBeOpened(location, item) {
	return location && isUrlInChildren(item, location.pathname);
}

function isUrlInChildren(parent, url) {
	if (!parent.children) {
		return false;
	}

	for (let i = 0; i < parent.children.length; i += 1) {
		if (parent.children[i].children) {
			if (isUrlInChildren(parent.children[i], url)) {
				return true;
			}
		}

		if (parent.children[i].url === url || url.includes(parent.children[i].url)) {
			return true;
		}
	}

	return false;
}

function FuseNavVerticalCollapse(props: FuseNavProps) {
	const [open, setOpen] = useState(() => needsToBeOpened(props.location, props.item));
	const { item, nestedLevel, onItemClick } = props;
	const itemPadding = nestedLevel > 0 ? 28 + nestedLevel * 16 : 12;

	const location = useLocation();

	useEffect(() => {
		if (needsToBeOpened(location, props.item)) {
			if (!open) {
				setOpen(true);
			}
		}
		// eslint-disable-next-line
	}, [location, props.item]);

	return useMemo(
		() => (
			<Root className={clsx(open && 'open')} itemPadding={itemPadding}>
				<ListItemButton
					className="fuse-list-item"
					onClick={() => setOpen(!open)}
					component={item.url ? NavLinkAdapter : 'li'}
					to={item.url}
					role="button"
				>
					{item.icon && (
						<Icon
							icon={item.icon}
							className={clsx('fuse-list-item-icon mr-12 shrink-0 text-20', item.iconClass)}
							color="action"
						/>
					)}

					<ListItemText
						className="fuse-list-item-text"
						primary={item.title}
						classes={{ primary: 'text-13 font-medium' }}
					/>

					{item.badge && <FuseNavBadge className="mx-4" badge={item.badge} />}

					<IconButton
						disableRipple
						className="-mx-12 h-40 w-40 p-0 hover:bg-transparent focus:bg-transparent"
						onClick={ev => ev.preventDefault()}
						size="large"
					>
						<Icon
							icon={open ? 'expand-less' : 'expand-more'}
							className="arrow-icon text-16"
							color="inherit"
						/>
					</IconButton>
				</ListItemButton>

				{item.children && (
					<Collapse in={open} className="collapse-children">
						{item.children.map(_item => (
							<FuseNavItem
								key={_item.id}
								type={`vertical-${_item.type}`}
								item={_item}
								nestedLevel={nestedLevel + 1}
								onItemClick={onItemClick}
							/>
						))}
					</Collapse>
				)}
			</Root>
		),
		[
			item.badge,
			item.children,
			item.icon,
			item.iconClass,
			item.title,
			item.url,
			itemPadding,
			nestedLevel,
			onItemClick,
			open
		]
	);
}

const NavVerticalCollapse = FuseNavVerticalCollapse;

export default NavVerticalCollapse;
